import { useComputed } from "@suse-o11y/signal";
import { useMemo } from "react";

import { type Dto, Type, WebSocketStreamType } from "~/models/dto";
import { identity } from "~/tools/identity";
import { isDeepEqual } from "~/tools/is-deep-equal";
import type { StreamOutputFromManager } from "~/tools/stream-manager";
import type { StreamOutput } from "~/tools/stream-manager/output";
import { STREAM_OUTPUT_LOADING } from "~/tools/stream-manager/store";
import { useEffect } from "~/tools/use-effect";

import { manager } from "./manager";
import { useWebSocketContext } from "./websocket-context";

/**
 * A stream is configured by a specific configuration object.
 * They related to each other as 1-to-1, so the configuration defines the stream type.
 * This map is a definition of the relation between the configuration and the stream type.
 */
const WEB_SOCKET_STREAM_REQUEST_TO_TYPE = {
	[Type.QueryStreamRequest]: WebSocketStreamType.ViewSnapshotDiffStream,
	[Type.StatusUpdateRequest]: WebSocketStreamType.StatusUpdateStream,
	[Type.SubjectPermissionsRequest]: WebSocketStreamType.PermissionStream,
} as const;

type WebSocketStreamRequestToResponse = typeof WEB_SOCKET_STREAM_REQUEST_TO_TYPE;

/**
 * Those are configuration object for the stream.
 *
 * It infers the typing out of the request type.
 * So you want to update WEB_SOCKET_STREAM_REQUEST_TO_TYPE to modify the type.
 */
export type WebSocketStreamRequest = Extract<Dto, { _type: keyof WebSocketStreamRequestToResponse }>;

type StreamOutputFromRequest<TRequest extends WebSocketStreamRequest> = StreamOutputFromManager<
	WebSocketStreamRequestToResponse[TRequest["_type"]],
	typeof manager
>;

type ExtractStreamOutputData<TOutput> =
	TOutput extends StreamOutput<unknown, infer TData> ? Exclude<TData, undefined> : never;

type ExtractStreamOutputError<TOutput> =
	TOutput extends StreamOutput<infer TError, unknown> ? Exclude<TError, undefined> : never;

interface UseWebSocketStreamOptions<TRequest> {
	request: undefined | TRequest;
}

interface UseWebSocketStreamOptionsWithSelect<TRequest extends WebSocketStreamRequest, TOutput>
	extends UseWebSocketStreamOptions<TRequest> {
	/**
	 * Selects the data from the stream output when it is available (!= undefined).
	 */
	select: (data: ExtractStreamOutputData<StreamOutputFromRequest<TRequest>>) => TOutput;
}

export function useWebSocketStream<TRequest extends WebSocketStreamRequest, TOutput>(
	options: UseWebSocketStreamOptionsWithSelect<TRequest, TOutput>,
): StreamOutput<ExtractStreamOutputError<StreamOutputFromRequest<TRequest>>, TOutput>;

export function useWebSocketStream<TRequest extends WebSocketStreamRequest>(
	options: UseWebSocketStreamOptions<TRequest>,
): StreamOutputFromRequest<TRequest>;

export function useWebSocketStream({
	request,
	select = identity,
}: UseWebSocketStreamOptionsWithSelect<WebSocketStreamRequest, unknown>): StreamOutput<unknown, unknown> {
	const { store, registry } = useWebSocketContext();

	useEffect(
		() => {
			if (request != null) {
				const streamType = WEB_SOCKET_STREAM_REQUEST_TO_TYPE[request._type];

				registry.write((current) => manager.subscribe({ streamType, request }, current));

				return () => {
					registry.write((current) => manager.unsubscribe({ streamType, request }, current));
				};
			}
		},
		[request, registry],
		[isDeepEqual, isDeepEqual],
	);

	/**
	 * Extract the data from the store so {@link select} receives a stable reference.
	 */
	const { data, ...output } = useComputed(
		(monitor) => {
			if (request == null) {
				return STREAM_OUTPUT_LOADING;
			}

			const streamType = WEB_SOCKET_STREAM_REQUEST_TO_TYPE[request._type];

			return manager.get({ streamType, request }, store.read(monitor));
		},

		[store, request],
	);

	return {
		...output,
		data: useMemo(() => (data === undefined ? data : select(data)), [data, select]),
	} as StreamOutput<unknown, unknown>;
}
